if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button')?.addEventListener('click', (event) => {
            event.preventDefault();
            this.setActiveMedia(mediaToSwitch.dataset.target, false, {
              scrollPage: false,
              lockThumbnailDuringScroll: true,
            });
          });
        });

        this.setInitialThumbnail();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      setInitialThumbnail() {
        const liquidSelectedThumbnail = this.elements.thumbnails
          .querySelector('button[aria-current]')
          ?.closest('[data-target]');
        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        const activeThumbnail =
          liquidSelectedThumbnail ||
          (activeMedia
            ? this.elements.thumbnails.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`)
            : this.elements.thumbnails.querySelector('[data-target]'));

        this.setActiveThumbnail(activeThumbnail, { scroll: false });
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;
        if (this.lockedThumbnailMediaId) {
          if (mediaId === this.lockedThumbnailMediaId) this.unlockThumbnail();
          return;
        }

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        const settings = {
          scrollPage: true,
          scrollThumbnail: true,
          lockThumbnailDuringScroll: false,
          instant: false,
          ...options,
        };

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          activeThumbnail?.parentElement.firstChild !== activeThumbnail &&
            activeThumbnail?.parentElement.prepend(activeThumbnail);

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        if (settings.lockThumbnailDuringScroll) this.lockThumbnail(mediaId);

        window.setTimeout(() => {
          if (settings.instant) {
            const track = activeMedia.parentElement;
            const previousBehavior = track.style.scrollBehavior;
            const previousSnapType = track.style.scrollSnapType;

            // Disable BOTH the smooth-scroll AND the scroll-snap enforcement.
            // With snap still mandatory, the browser will "correct" our jump to
            // the nearest snap point the instant smooth scrolling is restored —
            // that correction is what caused the half-cut/slick-track glitch.
            track.style.scrollSnapType = 'none';
            track.style.scrollBehavior = 'auto';
            track.scrollLeft = activeMedia.offsetLeft;

            // Wait two animation frames before restoring — gives the browser
            // time to fully settle the scroll position first, so snap has
            // nothing left to "correct" once it's re-enabled.
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                track.style.scrollSnapType = previousSnapType;
                track.style.scrollBehavior = previousBehavior;
              });
            });
          } else {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: 'smooth',
            });
          }

          if (!settings.scrollPage) return;

          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;

          window.scrollTo({
            top: activeMediaRect.top + window.scrollY,
            behavior: 'smooth',
          });
        });
        this.playActiveMedia(activeMedia);

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail, { scroll: settings.scrollThumbnail, instant: settings.instant });
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail, options = {}) {
        if (!this.elements.thumbnails || !thumbnail) return;

        // Prevent unnecessary re-scroll
        if (thumbnail.classList.contains('is-selected')) return;

        const settings = {
          scroll: true,
          instant: false,
          ...options,
        };

        this.elements.thumbnails
          .querySelectorAll('.thumbnail-list__item')
          .forEach((item) => {
            item.classList.remove('is-selected');
            item.querySelector('button')?.removeAttribute('aria-current');
          });

        thumbnail.classList.add('is-selected');
        thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');

        if (!settings.scroll) return;

        this.scrollThumbnailIntoView(thumbnail, settings.instant);
      }

      lockThumbnail(mediaId) {
        clearTimeout(this.thumbnailLockTimeout);
        this.lockedThumbnailMediaId = mediaId;
        this.thumbnailLockTimeout = setTimeout(() => this.unlockThumbnail(), 900);
      }

      unlockThumbnail() {
        clearTimeout(this.thumbnailLockTimeout);
        this.lockedThumbnailMediaId = null;
      }

      scrollThumbnailIntoView(thumbnail, instant = false) {
        const thumbnailList = this.elements.thumbnails.slider;
        if (!thumbnailList || !thumbnail) return;

        const targetScroll =
          thumbnail.offsetLeft - (thumbnailList.clientWidth / 2) + (thumbnail.clientWidth / 2);

        if (instant) {
          const previousBehavior = thumbnailList.style.scrollBehavior;
          const previousSnapType = thumbnailList.style.scrollSnapType;

          thumbnailList.style.scrollSnapType = 'none';
          thumbnailList.style.scrollBehavior = 'auto';
          thumbnailList.scrollLeft = targetScroll;

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              thumbnailList.style.scrollSnapType = previousSnapType;
              thumbnailList.style.scrollBehavior = previousBehavior;
            });
          });
        } else {
          thumbnailList.scrollTo({ left: targetScroll, behavior: 'smooth' });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}